import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lightweight popover menu used by the project kebabs (cards + rows).
 *
 * Usage:
 *   <Menu
 *     trigger={(toggle, open) => (
 *       <IconBtn name="kebab" label="More" onClick={(e) => { e.stopPropagation(); toggle(e); }} />
 *     )}
 *     items={[
 *       { label: 'View details', onClick: () => onSelect(project) },
 *       { divider: true },
 *       { label: 'Delete', danger: true, onClick: () => onDelete(project) },
 *     ]}
 *   />
 *
 * Behavior:
 *   - Anchored to the trigger element via getBoundingClientRect; right-
 *     aligned so the menu never extends past the trigger.
 *   - Closes on outside click, on Escape, on item click, and on scroll.
 *   - Rendered into document.body (portal) so it isn't clipped by card
 *     overflow:hidden or any z-index parents.
 *
 * Items shape:
 *   { label, onClick, danger?, icon?, disabled?, divider? }
 */
export default function Menu({ trigger, items, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const el = e?.currentTarget || triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAnchor({ rect });
    setOpen((o) => !o);
  }, []);

  // Click outside + escape + scroll → close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        close();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    const onScroll = () => close();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  // Compute menu position from anchor. Right-aligned by default; left-
  // aligned if the menu would extend past the viewport on the left.
  const style = (() => {
    if (!anchor) return { display: 'none' };
    const { rect } = anchor;
    const top = rect.bottom + 4;
    if (align === 'left') {
      return { position: 'fixed', top, left: rect.left };
    }
    return { position: 'fixed', top, right: window.innerWidth - rect.right };
  })();

  return (
    <span ref={triggerRef} style={{ display: 'inline-flex' }}>
      {trigger(toggle, open)}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              ...style,
              minWidth: 168,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-default)',
              borderRadius: 8,
              boxShadow: 'var(--shadow-md)',
              padding: 4,
              zIndex: 1000,
              fontSize: 14.5,
            }}
          >
            {items.map((item, i) =>
              item.divider ? (
                <div
                  key={`d-${i}`}
                  style={{
                    height: 1,
                    background: 'var(--border-subtle)',
                    margin: '4px 6px',
                  }}
                />
              ) : (
                <MenuItem
                  key={`i-${i}`}
                  item={item}
                  onPick={() => {
                    if (item.disabled) return;
                    close();
                    item.onClick && item.onClick();
                  }}
                />
              )
            )}
          </div>,
          document.body
        )}
    </span>
  );
}

function MenuItem({ item, onPick }) {
  const [hover, setHover] = useState(false);
  const color = item.danger ? 'var(--danger)' : 'var(--text-1)';
  const bg = hover && !item.disabled ? 'var(--bg-hover)' : 'transparent';
  // The Menu's outer dropdown is portal'd to document.body, but React
  // events still bubble through the React virtual tree — so without
  // stopPropagation here, clicking 'Delete project' (or any menu item)
  // bubbles back up to the ProjectCard's onClick={onSelect(project)}
  // and navigates into the project before the async confirm dialog
  // even resolves. mousedown is also stopped so the Menu's outside-
  // click listener (which uses mousedown) doesn't fire from inside
  // and re-trigger close() during the React event cycle.
  const handleClick = (e) => {
    e.stopPropagation();
    onPick();
  };
  return (
    <button
      type="button"
      role="menuitem"
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={item.disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '7px 10px',
        border: 'none',
        background: bg,
        color,
        opacity: item.disabled ? 0.5 : 1,
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        borderRadius: 5,
        textAlign: 'left',
        fontSize: 14.5,
        fontWeight: 500,
        transition: 'background .1s',
      }}
    >
      {item.icon}
      <span>{item.label}</span>
    </button>
  );
}
